<template>
  <div class="container">
    <template v-if="!data || !data.length">
      <div class="no-data-label">No data available</div>
    </template>
    <template v-else>
      <div
        :class="[
          'collection-container',
          {
            'is-loading': isLoading,
          },
        ]"
      >
        <CollectionItem
          v-for="(item, index) of data"
          :key="index"
          :item="item"
          :isCanUpdate="isCanUpdate"
          @openImage="openImage(item)"
          @viewDetail="viewDetail"
          @submit="handleChangeStatus"
        />
      </div>

      <v-pagination
        :value="meta.current_page"
        :length="meta.total_page"
        :total-visible="9"
        @input="$emit('update:page', $event)"
        class="mt-4"
      ></v-pagination>
    </template>

    <v-dialog
      v-model="dialogPreviewImage"
      :width="dialogWidth"
      max-width="90dvw"
      @keydown.esc="cancel"
    >
      <div class="dialog-preview-image-container">
        <v-row v-bind="{ 'no-gutters': true }">
          <v-col cols="12" md="6">
            <div class="dialog-preview-image">
              <image-zoom
                :regular="pic"
                :zoom-amount="4"
                :click-zoom="true"
                img-class="img-fluid-zoom"
                close-pos="top-right"
                message-pos="top"
              ></image-zoom>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div v-if="dialogContent">
              <div class="dialog-content">
                <div class="dialog-header-content">
                  <div class="collection-product-name">
                    {{ dialogContent.name }}
                  </div>
                  <v-btn
                    icon
                    small
                    @click="dialogPreviewImage = false"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </div>

                <div class="dialog-body-content">
                  <div>
                    <span class="label"> สถานะ: </span>
                    <span class="content">
                      <div
                        :class="[
                          'status-dot',
                          handleStatus(dialogContent).class,
                        ]"
                      ></div>
                      {{ handleStatus(dialogContent).label }}
                    </span>
                  </div>
                  <div>
                    <span class="label"> โปรดักโค้ด: </span>
                    <span class="content">
                      {{ dialogContent.sku }}
                    </span>
                  </div>
                  <div>
                    <span class="label"> ซีเรียล: </span>
                    <span class="content">
                      {{ dialogContent.serial }}
                    </span>
                  </div>
                  <div class="wrap-text-2-line">
                    <span class="label"> สาขา: </span>
                    <span class="content">
                      {{
                        dialogContent.store_name.replace(/_/g, ' ')
                      }}
                    </span>
                  </div>
                  <div>
                    <span class="label"> พนักงาน: </span>
                    <span class="content">
                      {{ dialogContent.emp_name }}
                    </span>
                  </div>
                  <div>
                    <span class="label"> อัปเดต: </span>
                    <span class="content">
                      {{ dialogContent.created_timestamp }}
                    </span>
                  </div>
                  <div
                    v-if="
                      !dialogContent.answer &&
                      dialogContent.explanation !== '-'
                    "
                    class="explain-box"
                  >
                    <span class="label">
                      คำอธิบาย โดย AI
                      <div class="badge-beta">{{ VERSION.AI }}</div>
                      :
                    </span>
                    <div class="content">
                      {{ dialogContent.explanation }}
                    </div>
                  </div>
                </div>

                <div class="dialog-actions-content">
                  <v-btn
                    v-if="isCanUpdate && !dialogContent.answer"
                    size="small"
                    color="primary"
                    depressed
                    class="rounded-lg btn-action"
                    @click="handleChangeStatus(dialogContent)"
                  >
                    ไม่ผิดปกติ!
                  </v-btn>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </div>
    </v-dialog>

    <v-dialog
      v-model="dialogSubmit"
      width="340px"
      @keydown.esc="cancel"
    >
      <v-card class="py-4 px-4 rounded-lg">
        <div class="title">ยืนยันการดำเนินการ</div>
        <div class="my-4">
          กรุณายืนยันว่าคุณได้ทำการตรวจสอบแล้ว
          และไม่พบความผิดปกติจากรูปภาพดังกล่าว
        </div>
        <v-row>
          <v-col cols="6">
            <v-btn
              block
              depressed
              @click="dialogSubmit = false"
              class="rounded-lg"
              >ยกเลิก</v-btn
            >
          </v-col>
          <v-col cols="6">
            <v-btn
              block
              depressed
              color="primary"
              class="rounded-lg"
              @click="handleConfirmChangeStatus"
              >ยืนยันไม่ผิดปกติ</v-btn
            >
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CollectionItem from '@/components/CollectionItem'
import VERSION from '@/enums/version'

export default {
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    meta: {
      type: Object,
      default: () => {},
    },
    isCanUpdate: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    CollectionItem,
  },

  computed: {
    dialogWidth() {
      // You can adjust these values based on your design requirements
      if (this.$vuetify.breakpoint.xsOnly) {
        return '100dvw' // Smaller on small screens
      } else {
        return '80dvw' // Fixed size on md and larger screens
      }
    },
  },

  data() {
    return {
      VERSION,
      footerProps: { 'items-per-page-options': [10, 20, 50, 100] },
      dialogPreviewImage: false,
      dialogSubmit: false,
      pic: '',
      footer_props: {
        'items-per-page-options': [],
        'items-per-page-text': null,
        'disable-items-per-page': true,
      },
      currentPage: 1,
      itemsPerPage: 20, // or whatever number you want
      totalItems: this.data?.length, // you will need to set this based on your data
      form: {
        id: null,
      },
      dialogContent: null,
    }
  },

  methods: {
    async openImage(event) {
      if (event.image_url) {
        this.pic = await event.image_url
        this.dialogContent = await event
        this.dialogPreviewImage = true
      }
    },
    viewDetail(data) {
      this.$router.push({
        name: 'StoreDetail',
        params: data,
      })
    },
    getScanBarcode(val) {
      return val === 1
    },
    handleChangeStatus(item) {
      this.dialogSubmit = true
      this.form.id = item.id
    },
    handleConfirmChangeStatus() {
      this.$emit('submit', this.form)
      this.dialogSubmit = false
    },
    handleStatus(event) {
      return {
        class: !event.answer
          ? event.explanation !== '-'
            ? 'fail'
            : 'warning'
          : '',
        label: !event.answer
          ? event.explanation !== '-'
            ? 'ผิดปกติ'
            : 'ไม่ได้ประมวลผล'
          : 'ปกติ',
      }
    },
  },
}
</script>

<style lang="scss">
.collection-container {
  display: grid;
  gap: 20px;

  @media screen and (min-width: 1280px) {
    grid-template-columns: repeat(5, 1fr);
  }

  @media screen and (max-width: 1279px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media screen and (max-width: 767px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: 479px) {
    grid-template-columns: repeat(1, 1fr);
  }
}

.no-data-label {
  width: 100%;
  height: 60dvh;
  display: flex;
  align-items: center;
  justify-content: center;

  text-align: center;
  color: rgba(0, 0, 0, 0.38);
}

.dialog-content {
  text-align: left;
  max-width: 100%;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  .dialog-header-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;

    .collection-product-name {
      font-size: 18px;
      font-weight: bold;
      color: #000;
      line-height: 20px;
      max-height: fit-content;
    }
  }

  .dialog-body-content {
    display: grid;
    gap: 8px;
    align-items: flex-end;

    font-size: 14px;
    margin-bottom: auto;
    color: #000;

    .label {
      font-weight: lighter;
    }

    .content {
      font-weight: 500;
    }

    .explain-box {
      background-color: #f4f4f4;
      padding: 8px;
      border-radius: 4px;
    }
  }

  .dialog-actions-content {
    display: flex;
    justify-content: flex-end;
    border-top: 1px solid #e8e8e8;
    padding-top: 16px;

    .btn-action {
      width: 100%;

      @media screen and (min-width: 768px) {
        width: fit-content;
      }
    }
  }
}
.dialog-preview-image-container {
  background-color: #fff;

  .dialog-preview-image {
    display: grid;
    align-items: center;
    min-height: 100%;
    background-color: #000;
    padding: 16px;
  }
}
</style>
